import { RefObject, useEffect, useRef } from 'react'

interface UseInfiniteScrollProps {
  targetRef: RefObject<Element | null>
  canLoad: boolean
  onLoad: () => void | Promise<void>
  onLoadComplete?: () => void
  rootMargin?: string
  trigger?: boolean
}

export const useInfiniteScroll = ({
  targetRef,
  canLoad,
  onLoad,
  onLoadComplete,
  rootMargin = '150px',
  trigger,
}: UseInfiniteScrollProps) => {
  const loadingRef = useRef(false)
  const canLoadRef = useRef(canLoad)
  const onLoadRef = useRef(onLoad)
  const onLoadCompleteRef = useRef(onLoadComplete)

  useEffect(() => {
    canLoadRef.current = canLoad
    onLoadRef.current = onLoad
    onLoadCompleteRef.current = onLoadComplete
  })

  useEffect(() => {
    const node = targetRef?.current
    if (!node) return

    const observer = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            canLoadRef.current &&
            !loadingRef.current
          ) {
            loadingRef.current = true
            await onLoadRef.current()
            onLoadCompleteRef.current?.()
            loadingRef.current = false
          }
        }
      },
      { rootMargin },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [targetRef, rootMargin, trigger])
}
